/*
 * Страница PostgreSQL (Resource postgres-cluster): «Инстансы» — узлы кластера CloudNativePG
 * (роль, состояние, узел Kubernetes, адрес), «Базы и пользователи» — то, что описано в IaC
 * (initdb кластера, CR Database и DatabaseRole из values.yaml в gitops) с состоянием
 * применения оператором; размер базы — из Prometheus, базы не из IaC — предупреждением.
 */
import { TableCell, TableRow, Tooltip } from '@material-ui/core';
import {
  Link,
  StatusError,
  StatusOK,
  StatusPending,
  StatusWarning,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { PgDatabase, PgInstance, PgUser } from '../../../lib/datastores';
import { formatBytes } from '../../../lib/prometheus';
import { PostgresIcon } from '../../icons';
import {
  CardTitle,
  DatastoreCard,
  DsTable,
  Fact,
  PodStatus,
  useDsStyles,
} from './shared';
import { PostgresData, usePostgresState } from './useDatastoreData';

const Health = ({ value }: { value: string }) => {
  switch (value) {
    case 'healthy':
      return <StatusOK>healthy</StatusOK>;
    case 'replicating':
      return <StatusPending>replicating</StatusPending>;
    case 'failed':
      return <StatusError>failed</StatusError>;
    default:
      return <StatusWarning>{value}</StatusWarning>;
  }
};

const InstanceRow = ({ i }: { i: PgInstance }) => {
  const classes = useDsStyles();
  return (
    <TableRow>
      <TableCell className={classes.nowrap}>
        <b>{i.name}</b>
      </TableCell>
      <TableCell className={classes.nowrap}>
        {i.role === 'primary' ? <b>primary</b> : i.role}
      </TableCell>
      <TableCell className={classes.nowrap}>
        <Health value={i.health} />
      </TableCell>
      <TableCell className={classes.nowrap}>
        <PodStatus ready={i.ready} phase={i.phase} />
      </TableCell>
      <TableCell className={classes.mono}>{i.podIp ?? '—'}</TableCell>
      <TableCell className={classes.nowrap}>{i.node ?? '—'}</TableCell>
      <TableCell className={classes.muted}>{i.restarts}</TableCell>
    </TableRow>
  );
};

export const PostgresInstancesCard = () => {
  const { entity } = useEntity();
  const state = usePostgresState();
  const classes = useDsStyles();
  return (
    <DatastoreCard<PostgresData>
      title={
        <CardTitle
          icon={<PostgresIcon fontSize="small" />}
          text="Инстансы PostgreSQL"
        />
      }
      subheader={d => d.summary.phase}
      state={state}
      errorTitle="Не удалось загрузить инстансы PostgreSQL"
    >
      {d => (
        <>
          <div className={classes.facts}>
            <Fact
              label="Готово"
              value={`${d.summary.readyInstances}/${d.summary.instances}`}
            />
            {d.summary.primary && (
              <Fact label="Primary" value={d.summary.primary} />
            )}
            {d.summary.version && (
              <Fact label="PostgreSQL" value={d.summary.version} />
            )}
            {d.summary.storage && (
              <Fact label="Диск инстанса" value={d.summary.storage} />
            )}
            {(d.summary.cpu || d.summary.memory) && (
              <Fact
                label="Ресурсы"
                value={[
                  d.summary.cpu && `${d.summary.cpu} CPU`,
                  d.summary.memory,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            )}
          </div>
          <DsTable
            head={[
              'Инстанс',
              'Роль',
              'Состояние',
              'Под',
              'Адрес пода',
              'Узел Kubernetes',
              'Перезапуски',
            ]}
            empty={
              d.instances.length === 0
                ? 'Подов инстансов пока нет — кластер разворачивается'
                : undefined
            }
          >
            {d.instances.map(i => (
              <InstanceRow key={i.name} i={i} />
            ))}
          </DsTable>
          <div className={classes.foot}>
            Источник: Cluster CloudNativePG и поды namespace datastores через
            GitLab Agent. Адрес для приложений — сервис записи{' '}
            <span className={classes.mono}>{`${
              entity.metadata.annotations?.['viva.company.com/endpoint'] ?? ''
            }`}</span>
          </div>
        </>
      )}
    </DatastoreCard>
  );
};

const RoleStatus = ({ status }: { status?: string }) => {
  const classes = useDsStyles();
  if (!status) return <span className={classes.muted}>—</span>;
  return status === 'reconciled' ? (
    <StatusOK>reconciled</StatusOK>
  ) : (
    <StatusWarning>{status}</StatusWarning>
  );
};

/** Состояние CR Database/DatabaseRole: применён ли оператором. */
const AppliedStatus = ({
  applied,
  message,
}: {
  applied?: boolean;
  message?: string;
}) => {
  if (applied) return <StatusOK>применена</StatusOK>;
  if (applied === false) {
    return (
      <Tooltip title={message ?? ''}>
        <span style={{ whiteSpace: 'nowrap' }}>
          <StatusError>ошибка</StatusError>
        </span>
      </Tooltip>
    );
  }
  return <StatusPending>применяется</StatusPending>;
};

const flags = (u: PgUser) =>
  [u.login && 'login', u.superuser && 'superuser', u.createdb && 'createdb']
    .filter(Boolean)
    .join(', ') || '—';

/** Откуда известен объект: initdb кластера или CR с именем. */
const Origin = ({ kind, resource }: { kind: string; resource?: string }) => {
  const classes = useDsStyles();
  if (!resource) return <span className={classes.muted}>{kind}</span>;
  return (
    <span className={classes.nowrap}>
      <span className={classes.muted}>{kind} · </span>
      <span className={classes.mono}>{resource}</span>
    </span>
  );
};

const userOrigin = (u: PgUser) => {
  switch (u.source) {
    case 'owner':
      return <Origin kind="владелец первой базы (initdb)" />;
    case 'superuser':
      return <Origin kind="суперпользователь кластера" />;
    case 'role':
      return <Origin kind="DatabaseRole" resource={u.resource} />;
    default:
      return <Origin kind="managed.roles кластера" />;
  }
};

const SizeCell = ({ size, error }: { size?: number; error?: Error }) => {
  const classes = useDsStyles();
  if (size !== undefined) return <>{formatBytes(size)}</>;
  if (error) {
    return (
      <Tooltip title={error.message}>
        <span className={classes.muted}>Prometheus недоступен</span>
      </Tooltip>
    );
  }
  return <span className={classes.muted}>—</span>;
};

const DatabaseRow = ({ db, data }: { db: PgDatabase; data: PostgresData }) => {
  const classes = useDsStyles();
  return (
    <TableRow>
      <TableCell className={classes.nowrap}>
        <b className={classes.mono}>{db.name}</b>
      </TableCell>
      <TableCell className={classes.mono}>{db.owner ?? '—'}</TableCell>
      <TableCell className={classes.mono}>
        {db.extensions.length ? (
          db.extensions.join(', ')
        ) : (
          <span className={classes.muted}>—</span>
        )}
      </TableCell>
      <TableCell className={classes.nowrap}>
        <SizeCell
          size={data.sizes.value?.get(db.name)}
          error={data.sizes.error}
        />
      </TableCell>
      <TableCell>
        {db.source === 'bootstrap' ? (
          <Origin kind="первая база (initdb)" />
        ) : (
          <Origin kind="Database" resource={db.resource} />
        )}
      </TableCell>
      <TableCell className={classes.nowrap}>
        {db.source === 'bootstrap' ? (
          <span className={classes.muted}>—</span>
        ) : (
          <AppliedStatus applied={db.applied} message={db.message} />
        )}
      </TableCell>
    </TableRow>
  );
};

const UserRow = ({ u }: { u: PgUser }) => {
  const classes = useDsStyles();
  return (
    <TableRow>
      <TableCell className={classes.nowrap}>
        <b className={classes.mono}>{u.name}</b>
        {u.comment && (
          <div className={classes.muted} style={{ fontSize: 12 }}>
            {u.comment}
          </div>
        )}
      </TableCell>
      <TableCell className={classes.nowrap}>{flags(u)}</TableCell>
      <TableCell className={classes.mono}>
        {u.secret ? `datastores/${u.secret}` : '—'}
      </TableCell>
      <TableCell>{userOrigin(u)}</TableCell>
      <TableCell className={classes.nowrap}>
        {u.source === 'role' && (
          <AppliedStatus applied={u.applied} message={u.message} />
        )}
        {u.source === 'managed' && <RoleStatus status={u.status} />}
        {(u.source === 'owner' || u.source === 'superuser') && (
          <span className={classes.muted}>—</span>
        )}
      </TableCell>
    </TableRow>
  );
};

export const PostgresDatabasesCard = () => {
  const { entity } = useEntity();
  const state = usePostgresState();
  const classes = useDsStyles();
  const valuesLink = entity.metadata.links?.find(l =>
    l.title?.startsWith('Values'),
  );
  return (
    <DatastoreCard<PostgresData>
      title={
        <CardTitle
          icon={<PostgresIcon fontSize="small" />}
          text="Базы и пользователи"
        />
      }
      state={state}
      errorTitle="Не удалось загрузить базы PostgreSQL"
    >
      {d => (
        <>
          <DsTable
            head={[
              'База',
              'Владелец',
              'Расширения',
              'Размер',
              'Откуда',
              'Состояние',
            ]}
            empty={
              d.databases.length === 0 ? 'Баз в IaC не описано' : undefined
            }
          >
            {d.databases.map(db => (
              <DatabaseRow key={db.name} db={db} data={d} />
            ))}
          </DsTable>
          {d.unmanaged.length > 0 && (
            <div className={classes.foot}>
              <StatusWarning>
                Есть в PostgreSQL, но не описаны в IaC:{' '}
                <span className={classes.mono}>{d.unmanaged.join(', ')}</span> —
                опишите в values.yaml или удалите
              </StatusWarning>
            </div>
          )}
          <div style={{ height: 16 }} />
          <DsTable
            head={[
              'Пользователь',
              'Права',
              'Секрет с паролем',
              'Откуда',
              'Состояние',
            ]}
            empty={
              d.users.length === 0
                ? 'Пользователей в IaC не описано'
                : undefined
            }
          >
            {d.users.map(u => (
              <UserRow key={u.name} u={u} />
            ))}
          </DsTable>
          <div className={classes.foot}>
            Базы и пользователи заводятся только через IaC: списки{' '}
            <span className={classes.mono}>databases</span> и{' '}
            <span className={classes.mono}>users</span> в{' '}
            {valuesLink ? (
              <Link to={valuesLink.url}>values.yaml кластера</Link>
            ) : (
              'values.yaml кластера'
            )}{' '}
            (viva/gitops) → Database и DatabaseRole CloudNativePG. Созданное
            руками внутри PostgreSQL здесь не видно и подлежит переносу в
            values.yaml. Пароли — в секретах namespace datastores.
          </div>
        </>
      )}
    </DatastoreCard>
  );
};
